import { nothing } from 'lit-html';
import { html, property } from 'lit-element';
import {
  KatLitElement,
  register,
  event,
  EventEmitter,
} from '../../shared/base';
import { offHtmlDirChange, onHtmlDirChange } from '../../utils/rtl-utils';
import { ifNotNull } from '../../utils/directives';
import { checkSlots } from '../../shared/slot-utils';
import baseStyles from '../../shared/base/base.lit.scss';
import {
  onBodyWeblabClassChange,
  offBodyWeblabClassChange,
} from '../../utils/weblab-helpers';
import expanderStyles from './expander.lit.scss';
import getString from './strings';

/**
 * @component {kat-expander} KatalExpander Expanders allow content to be broken into independent sections that can be expanded or collapsed. This allows content to be abbreviated for users when space is at a premium or when not all content is relevant for all users.
 * @example SampleExpander {"label": "My Expander", "content": "<div>My Content to Hide</div>"}
 * @example WithBadge {"label": "My Expander", "content": "<div slot=\"badge\"><kat-badge>New</kat-badge></div><div>My Content to Hide</div>"}
 * @guideline Do Content must be valid HTML. <div>Hello World</div>
 * @guideline Do Use expandable sections beneath relevant items in the interface to reduce page length.
 * @guideline Do Only include non-actionable and secondary content in the collapsed area of the expandable sections.
 * @guideline Do Use sentence case (not title case).
 * @guideline Do Keep heading content short and actionable by avoiding articles (such as "a," "an," "the").
 * @guideline Dont Don’t hide error messages or critical information under an expandable section.
 * @guideline Dont Don't use long text for badge labels. Badge labels have a max width of 150px.
 * @status Production
 * @htmlExample - <div>My Content here</div>
 * @theme flo
 * @slot label Contents will be used as the label (header text) section.
 * @slot badge Contents will be used as the header badge.
 * @slot toggle Contents will be used as the toggle indicator.
 * @slot default Contents will be used as the main collapsible content section.
 * @category Container
 * @a11y {keyboard}
 * @a11y {contrast}
 * @a11y {sr}
 */
@register('kat-expander')
export class KatExpander extends KatLitElement {
  /** Heading displayed for the expander */
  @property()
  label?: string;

  /** Determines if the content should be displayed */
  @property()
  expanded?: boolean;

  /** Text to display in an optional badge */
  @property({ attribute: 'badge-label' })
  badgeLabel?: string;

  /**
   * The type of badge to display to the user
   * @enum {value} default Used for a generic status or state
   * @enum {value} success Items that have positive meaning
   * @enum {value} warning Items with problems or that have been removed
   * @enum {value} info Items that are actionable
   */
  @property({ attribute: 'badge-type' })
  badgeType?: string;

  /** Fires when the expander is expanded. */
  @event('expand')
  private _expand: EventEmitter;

  /** Fires when the expander is collapsed. */
  @event('collapse')
  private _collapse: EventEmitter;

  static get styles() {
    return [baseStyles, expanderStyles];
  }

  connectedCallback() {
    super.connectedCallback();
    onHtmlDirChange(this._rerender);
    onBodyWeblabClassChange(this._rerender);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    offHtmlDirChange(this._rerender);
    offBodyWeblabClassChange(this._rerender);
  }

  private _rerender = () => {
    this.requestUpdate();
  };

  // This is needed to handle focus when slotted labels/titles get clicked
  focusHeader(e) {
    e.preventDefault();
    (this._shadow('.header') as HTMLElement).focus();
  }

  async toggleExpander() {
    this.expanded = !this.expanded;
    await this.updateComplete;
    this.expanded ? this._expand.emit() : this._collapse.emit();
  }

  private getExpanderIconName() {
    return this.getArrowDirection() === 'up' ? 'chevron-up' : 'chevron-down';
  }

  // TODO: After Visual Refresh experimentation, only up and down should be allowed
  private getArrowDirection() {
    return this.expanded ? 'up' : 'down';
  }

  render() {
    const slots = checkSlots(this);
    const expanderLabel =
      slots.label || this.label
        ? null
        : this.expanded
        ? getString('kat-expander-close')
        : getString('kat-expander-open');

    return html`
      <div class="wrapper">
        <button
          class="header"
          type="button"
          part="expander-header"
          aria-expanded=${this.expanded ? 'true' : 'false'}
          @click=${this.toggleExpander}
          @mousedown=${this.focusHeader}
          aria-label="${ifNotNull(expanderLabel)}"
        >
          <div class="header__text">
            <span class="label">
              <slot name="label" part="expander-label">
                ${this.label ? this.label : nothing}
              </slot>
            </span>
            <div class="header__badge">
              <slot name="badge" part="expander-badge">
                ${this.badgeLabel
                  ? html`
                      <kat-badge
                        .type=${this.badgeType}
                        variant="default"
                        .label=${this.badgeLabel}
                        class="badge"
                      ></kat-badge>
                    `
                  : nothing}
              </slot>
            </div>
          </div>
          <div
            class="header__toggle"
            part="expander-toggle"
            arrow="${this.getArrowDirection()}"
          >
            <slot name="toggle"
              ><kat-icon name=${this.getExpanderIconName()}></kat-icon
            ></slot>
          </div>
        </button>
      </div>
      <div class="content">
        <slot part="expander-content"></slot>
      </div>
    `;
  }
}
